import { useState, useEffect, useMemo } from "react";
import { ArrowLeft, Check, AlertTriangle, FileText } from "lucide-react";
import { api } from "../api";
import PatientAlias from "./PatientAlias.jsx";

// Склейка двух пациентов ScanApp в одного (окно «Разобрать», central-admin). Под каждым пациентом —
// его обследования и покупки с галочками: отмеченное переносится в основную карточку, снятое
// удаляется как дубль. Расходящиеся поля карточки выбираются явно. См. POST /api/patients/:id/merge.
const FIELDS = [
  ["name", "ФИО"], ["phone", "Телефон"], ["birthDate", "Дата рождения"], ["yclientsClientId", "Клиент YClients"],
  ["messenger", "Мессенджер"], ["messengerLink", "Ссылка на мессенджер"], ["socialNetwork", "Соц. сеть"],
  ["crmLink", "CRM"], ["notes", "Заметки"], ["internalNote", "Служебная заметка"],
];

const empty = v => v === null || v === undefined || v === "";
const fmtDate = v => v ? new Date(v).toLocaleDateString("ru-RU") : "—";
const purchaseKey = p => `${(p.name || "").trim().toLowerCase()}|${p.price}|${p.date}`;
const scanKey = s => `${fmtDate(s.createdAt)}|${s.footSide}|${s.footSizeMm}`;
const card = { boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" };

export default function PatientMergePanel({ firstId, secondId, onDone, onCancel, showToast }) {
  const [patients, setPatients] = useState(null);
  const [survivorId, setSurvivorId] = useState(null);
  const [keepDocs, setKeepDocs] = useState(new Set());
  const [keepPurchases, setKeepPurchases] = useState(new Set());
  const [fieldChoice, setFieldChoice] = useState({});
  const [purchaseDocs, setPurchaseDocs] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.getPatient(firstId), api.getPatient(secondId)]).then(([a, b]) => {
      setPatients([a, b]);
      // Основная — та, что уже привязана к YClients; иначе та, где больше обследований.
      const main = a.yclientsClientId && !b.yclientsClientId ? a : b.yclientsClientId && !a.yclientsClientId ? b : (a.scans.length >= b.scans.length ? a : b);
      setSurvivorId(main.id);
      setKeepDocs(new Set([...a.scans, ...b.scans].map(s => s.id)));
      setKeepPurchases(new Set([...a.purchases, ...b.purchases].map(p => p.id)));
    }).catch(e => setError(e.response?.data?.error || "Не удалось загрузить пациентов"));
  }, [firstId, secondId]);

  const survivor = patients?.find(p => p.id === survivorId);
  const other = patients?.find(p => p.id !== survivorId);

  const conflicts = useMemo(() => {
    if (!survivor || !other) return [];
    return FIELDS.filter(([key]) => !empty(survivor[key]) && !empty(other[key]) && String(survivor[key]) !== String(other[key]));
  }, [survivor, other]);

  // Совпадающие по названию/цене/дате покупки и по дате/стопе/размеру сканы у обоих — кандидаты в дубли.
  const duplicates = useMemo(() => {
    if (!patients) return { purchases: new Set(), scans: new Set() };
    const [a, b] = patients;
    const bp = new Set(b.purchases.map(purchaseKey)), bs = new Set(b.scans.map(scanKey));
    return {
      purchases: new Set(a.purchases.filter(p => bp.has(purchaseKey(p))).map(purchaseKey)),
      scans: new Set(a.scans.filter(s => bs.has(scanKey(s))).map(scanKey)),
    };
  }, [patients]);

  const toggle = (setter) => (id) => setter(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  if (error && !patients) return <div className="docs-empty">{error}</div>;
  if (!patients || !survivor) return <div className="docs-empty">Загрузка...</div>;

  const allScans = patients.flatMap(p => p.scans);
  const allPurchases = patients.flatMap(p => p.purchases);
  const droppedScans = allScans.filter(s => !keepDocs.has(s.id));
  const droppedPurchases = allPurchases.filter(p => !keepPurchases.has(p.id));
  const droppedWrittenOff = droppedPurchases.filter(p => p.yclientsWrittenOffAt);
  // Покупка со снятого (дублирующего) скана — по умолчанию к оставляемому скану-близнецу, если он есть.
  const orphaned = (pur) => pur.documentId && !keepDocs.has(pur.documentId) && keepPurchases.has(pur.id);
  const purchaseTarget = (pur) => {
    if (pur.id in purchaseDocs) return purchaseDocs[pur.id];
    const source = allScans.find(s => s.id === pur.documentId);
    const twin = source && allScans.find(s => s.id !== source.id && keepDocs.has(s.id) && scanKey(s) === scanKey(source));
    return twin?.id || "";
  };
  const keptScans = allScans.filter(s => keepDocs.has(s.id));
  const resolvedValue = (key) => (fieldChoice[key] === "other" || (fieldChoice[key] !== "survivor" && empty(survivor[key]))) ? other[key] : survivor[key];

  const handleMerge = async () => {
    const summary = [`Склеить в «${resolvedValue("name")}»?`, `Будет перенесено: обследований ${allScans.length - droppedScans.length}, покупок ${allPurchases.length - droppedPurchases.length}.`];
    if (droppedScans.length || droppedPurchases.length) summary.push(`Будет удалено как дубли: обследований ${droppedScans.length}, покупок ${droppedPurchases.length}.`);
    if (!window.confirm(summary.join("\n"))) return;
    setSaving(true);
    setError("");
    try {
      const fields = Object.fromEntries(FIELDS.map(([key]) => [key, fieldChoice[key] || (empty(survivor[key]) && !empty(other[key]) ? "other" : "survivor")]));
      const purchaseDocuments = Object.fromEntries(allPurchases.filter(orphaned).map(p => [p.id, purchaseTarget(p)]).filter(([, target]) => target));
      await api.mergePatients(survivor.id, { otherId: other.id, keepDocumentIds: [...keepDocs], keepPurchaseIds: [...keepPurchases], purchaseDocuments, fields });
      showToast?.("Пациенты склеены");
      onDone(survivor.id);
    } catch (e) {
      setError(e.response?.data?.error || "Не удалось склеить");
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <button className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 w-fit" onClick={onCancel}>
        <ArrowLeft className="h-3.5 w-3.5" /> Отмена
      </button>

      <div className="rounded-2xl bg-white p-4 flex flex-col gap-3" style={card}>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Основная карточка</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {patients.map(p => (
            <label key={p.id} className={`rounded-xl border px-4 py-3 cursor-pointer flex items-start gap-2 ${p.id === survivorId ? "border-blue-400 bg-blue-50/50" : "border-slate-200"}`}>
              <input type="radio" className="mt-1" checked={p.id === survivorId} onChange={() => { setSurvivorId(p.id); setFieldChoice({}); }} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 break-words">{p.name}<PatientAlias name={p.name} alias={p.alias} /></p>
                <p className="text-xs text-slate-400">{p.phone || "без телефона"} · создан {fmtDate(p.createdAt)}</p>
                {p.yclientsClientId && <span className="inline-block mt-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">YClients #{p.yclientsClientId}</span>}
              </div>
            </label>
          ))}
        </div>
        <p className="text-xs text-slate-400">Вторая карточка исчезнет, всё отмеченное ниже перейдёт в основную.</p>
      </div>

      {conflicts.length > 0 && (
        <div className="rounded-2xl bg-white p-4 flex flex-col gap-2" style={card}>
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Разные данные — выберите, что оставить</p>
          {conflicts.map(([key, label]) => (
            <div key={key} className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 py-2 border-b border-slate-50 last:border-0">
              <span className="text-xs text-slate-500 md:w-40 flex-shrink-0">{label}</span>
              {[["survivor", survivor], ["other", other]].map(([side, p]) => (
                <label key={side} className="flex items-center gap-1.5 text-sm text-slate-800 cursor-pointer min-w-0">
                  <input type="radio" name={`field-${key}`} checked={(fieldChoice[key] || "survivor") === side} onChange={() => setFieldChoice(c => ({ ...c, [key]: side }))} />
                  <span className="break-words">{String(p[key])}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[survivor, other].map(p => (
          <div key={p.id} className="rounded-2xl bg-white p-4 flex flex-col gap-3 min-w-0" style={card}>
            <div>
              <p className="text-sm font-bold text-slate-900 break-words">{p.name}<PatientAlias name={p.name} alias={p.alias} /></p>
              <p className="text-xs text-slate-400">{p.id === survivorId ? "основная" : "будет удалена"}</p>
            </div>

            <div className="flex flex-col gap-1">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Обследования ({p.scans.length})</p>
              {p.scans.length === 0 && <p className="text-xs text-slate-400 py-1">Нет</p>}
              {p.scans.map(s => (
                <label key={s.id} className={`flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-slate-50 ${keepDocs.has(s.id) ? "" : "opacity-50 line-through"}`}>
                  <input type="checkbox" className="mt-0.5" checked={keepDocs.has(s.id)} onChange={() => toggle(setKeepDocs)(s.id)} />
                  <div className="text-xs text-slate-700 min-w-0">
                    <span>{s.createdAt ? new Date(s.createdAt).toLocaleString("ru-RU") : "—"}</span>
                    {s.footSide && <span> · {s.footSide === "left" ? "левая" : s.footSide === "right" ? "правая" : s.footSide}</span>}
                    {s.footSizeMm && <span> · {s.footSizeMm} мм</span>}
                    {s.location && <span> · {s.location}</span>}
                    {s.operatorName && <span className="text-slate-400"> · провёл {s.operatorName}</span>}
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {s.hasReport && <Badge tone="slate"><FileText className="h-2.5 w-2.5 inline" /> отчёт</Badge>}
                      {s.yclientsRecordId && <Badge tone="emerald">запись #{s.yclientsRecordId}</Badge>}
                      {duplicates.scans.has(scanKey(s)) && <Badge tone="amber">похоже на дубль</Badge>}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="flex flex-col gap-1">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Покупки ({p.purchases.length})</p>
              {p.purchases.length === 0 && <p className="text-xs text-slate-400 py-1">Нет</p>}
              {p.purchases.map(pur => (
                <div key={pur.id}>
                <label className={`flex items-start gap-2 rounded-lg px-2 py-1.5 cursor-pointer hover:bg-slate-50 ${keepPurchases.has(pur.id) ? "" : "opacity-50 line-through"}`}>
                  <input type="checkbox" className="mt-0.5" checked={keepPurchases.has(pur.id)} onChange={() => toggle(setKeepPurchases)(pur.id)} />
                  <div className="text-xs text-slate-700 min-w-0">
                    <span className="break-words">{pur.name}</span>
                    <span className="text-emerald-600 font-semibold"> {pur.price != null ? `${Number(pur.price).toLocaleString("ru-RU")} ₽` : ""}</span>
                    <span className="text-slate-400"> · {fmtDate(pur.date)}{pur.location ? ` · ${pur.location}` : ""}</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {pur.yclientsWrittenOffAt && <Badge tone="emerald">списано в YClients</Badge>}
                      {duplicates.purchases.has(purchaseKey(pur)) && <Badge tone="amber">похоже на дубль</Badge>}
                    </div>
                  </div>
                </label>
                {orphaned(pur) && (
                  <select
                    className="ml-8 mb-1 max-w-[calc(100%-2rem)] rounded-md border border-slate-200 text-[11px] px-1.5 py-0.5"
                    value={purchaseTarget(pur)}
                    onChange={e => setPurchaseDocs(d => ({ ...d, [pur.id]: e.target.value }))}
                  >
                    <option value="">Скан покупки удаляется — оставить без обследования</option>
                    {keptScans.map(s => (
                      <option key={s.id} value={s.id}>
                        Перенести к обследованию {new Date(s.createdAt).toLocaleString("ru-RU")}{s.yclientsRecordId ? ` (запись #${s.yclientsRecordId})` : ""}
                      </option>
                    ))}
                  </select>
                )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {droppedWrittenOff.length > 0 && (
        <div className="rounded-xl bg-amber-50 text-amber-800 text-xs px-4 py-3 flex gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Снятые покупки уже списаны в YClients ({droppedWrittenOff.map(p => p.name).join(", ")}). Удаление в ScanApp не отменит списание и оплату в YClients.</span>
        </div>
      )}
      {error && <div className="rounded-xl bg-red-50 text-red-700 text-xs px-4 py-3">{error}</div>}

      <div className="flex flex-wrap items-center justify-end gap-3">
        <span className="text-xs text-slate-500">
          Удалится: обследований {droppedScans.length}, покупок {droppedPurchases.length}
        </span>
        <button className="btn btn-primary btn-sm flex items-center gap-1.5" disabled={saving} onClick={handleMerge}>
          <Check className="h-3.5 w-3.5" /> {saving ? "Склеиваем..." : "Склеить пациентов"}
        </button>
      </div>
    </div>
  );
}

function Badge({ tone, children }) {
  const tones = { slate: "text-slate-500 bg-slate-100", emerald: "text-emerald-700 bg-emerald-50", amber: "text-amber-700 bg-amber-50" };
  return <span className={`text-[10px] font-semibold rounded-full px-1.5 py-0.5 ${tones[tone]}`}>{children}</span>;
}
